import { useEffect, useState } from "react";
import api from "../lib/api";
import { formatIDR, formatDate, todayISO } from "../lib/format";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, Users, FileText, Calendar, Banknote, Settings as SettingsIcon, Printer, Download, Eye, Mail } from "lucide-react";
import { generatePayslipPDF } from "../lib/payslipPdf";
import PdfPreviewDialog from "../components/PdfPreviewDialog";

const MARITAL_OPTIONS = ["TK/0", "TK/1", "TK/2", "TK/3", "K/0", "K/1", "K/2", "K/3"];
const EMP_STATUS_LABEL = { tetap: "Tetap", kontrak: "Kontrak", harian: "Harian", magang: "Magang" };
const ATT_STATUS = ["hadir", "sakit", "izin", "alpha", "cuti", "libur"];
const ATT_COLOR = {
  hadir: "bg-emerald-100 text-emerald-700 border-emerald-200",
  sakit: "bg-blue-100 text-blue-700 border-blue-200",
  izin: "bg-yellow-100 text-yellow-800 border-yellow-200",
  alpha: "bg-red-100 text-red-700 border-red-200",
  cuti: "bg-purple-100 text-purple-700 border-purple-200",
  libur: "bg-slate-100 text-slate-600 border-slate-200",
};

const empBlank = () => ({
  employee_code: "", full_name: "", nik_ktp: "", npwp: "", email: "", phone: "", address: "",
  department: "", position: "", employment_status: "tetap", join_date: todayISO(),
  basic_salary: 0, marital_status: "TK/0", bank_name: "", bank_account_number: "", bank_account_holder: "",
  bpjs_kesehatan_no: "", bpjs_ketenagakerjaan_no: "", active: true,
});

const currentMonth = () => todayISO().slice(0, 7);

export default function Personalia() {
  const [tab, setTab] = useState("employees");

  return (
    <div className="space-y-6" data-testid="personalia-page">
      <div>
        <div className="text-[11px] font-medium uppercase tracking-widest text-slate-500">Modul Personalia</div>
        <h1 className="font-display text-3xl font-extrabold text-slate-900 tracking-tight">HR & Payroll</h1>
        <p className="text-sm text-slate-500 mt-1">Data karyawan, kontrak, absensi, dan payroll (PPh 21 TER PMK 168/2023 + BPJS Kesehatan & Ketenagakerjaan).</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="hr-tabs">
          <TabsTrigger value="employees" data-testid="tab-employees"><Users size={14} className="mr-1" />Data Karyawan</TabsTrigger>
          <TabsTrigger value="contracts" data-testid="tab-contracts"><FileText size={14} className="mr-1" />Kontrak</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance"><Calendar size={14} className="mr-1" />Absensi</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="tab-payroll"><Banknote size={14} className="mr-1" />Payroll</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-hr-settings"><SettingsIcon size={14} className="mr-1" />Setup BPJS</TabsTrigger>
        </TabsList>
        <TabsContent value="employees"><EmployeesTab /></TabsContent>
        <TabsContent value="contracts"><ContractsTab /></TabsContent>
        <TabsContent value="attendance"><AttendanceTab /></TabsContent>
        <TabsContent value="payroll"><PayrollTab /></TabsContent>
        <TabsContent value="settings"><HRSettingsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ==================== Employees Tab ==================== */
function EmployeesTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empBlank());
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/hr/employees").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empBlank()); setEditingId(null); setOpen(true); };
  const openEdit = (emp) => { setForm({ ...empBlank(), ...emp }); setEditingId(emp.id); setOpen(true); };

  const save = async () => {
    if (!form.employee_code.trim() || !form.full_name.trim()) return toast.error("No. Karyawan & Nama wajib diisi");
    try {
      const payload = { ...form, basic_salary: Number(form.basic_salary) || 0 };
      if (editingId) await api.put(`/hr/employees/${editingId}`, payload);
      else await api.post("/hr/employees", payload);
      toast.success(editingId ? "Karyawan diupdate" : "Karyawan ditambahkan");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus karyawan ini?")) return;
    try { await api.delete(`/hr/employees/${id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  return (
    <Card className="card-elevated"><CardContent className="p-4">
      <div className="flex justify-between mb-3">
        <div className="text-sm text-slate-500">{items.length} karyawan terdaftar</div>
        <Button onClick={openNew} className="bg-navy-900 hover:bg-navy-800 text-white" data-testid="emp-new-btn"><Plus size={14} className="mr-1" />Tambah Karyawan</Button>
      </div>
      <div className="overflow-x-auto">
        <Table data-testid="emp-table">
          <TableHeader>
            <TableRow>
              <TableHead>No. Kry</TableHead><TableHead>Nama</TableHead><TableHead>Departemen</TableHead>
              <TableHead>Jabatan</TableHead><TableHead>Status</TableHead>
              <TableHead>PTKP</TableHead><TableHead className="text-right">Gaji Pokok</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((e) => (
              <TableRow key={e.id} data-testid={`emp-row-${e.employee_code}`}>
                <TableCell className="font-mono text-xs font-semibold">{e.employee_code}</TableCell>
                <TableCell>
                  <div className="font-medium">{e.full_name}</div>
                  <div className="text-xs text-slate-500">{e.email || e.phone}</div>
                </TableCell>
                <TableCell>{e.department || "-"}</TableCell>
                <TableCell>{e.position || "-"}</TableCell>
                <TableCell><Badge className="bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-100">{EMP_STATUS_LABEL[e.employment_status]}</Badge></TableCell>
                <TableCell className="font-mono text-xs">{e.marital_status}</TableCell>
                <TableCell className="text-right font-mono">{formatIDR(e.basic_salary)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => openEdit(e)} data-testid={`emp-edit-${e.employee_code}`}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(e.id)} className="ml-1" data-testid={`emp-delete-${e.employee_code}`}><Trash2 size={14} className="text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Belum ada karyawan</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editingId ? "Edit" : "Tambah"} Karyawan</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><Label>No. Karyawan *</Label><Input value={form.employee_code} onChange={(e) => setForm({ ...form, employee_code: e.target.value })} data-testid="emp-code" /></div>
            <div><Label>Nama Lengkap *</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="emp-name" /></div>
            <div><Label>NIK KTP</Label><Input value={form.nik_ktp} onChange={(e) => setForm({ ...form, nik_ktp: e.target.value })} /></div>
            <div><Label>NPWP</Label><Input value={form.npwp} onChange={(e) => setForm({ ...form, npwp: e.target.value })} /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>No HP</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="col-span-2"><Label>Alamat</Label><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label>Departemen</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="Cth: Finance" /></div>
            <div><Label>Jabatan</Label><Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} placeholder="Cth: Staff Akunting" /></div>
            <div>
              <Label>Status Kepegawaian</Label>
              <Select value={form.employment_status} onValueChange={(v) => setForm({ ...form, employment_status: v })}>
                <SelectTrigger data-testid="emp-status"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(EMP_STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Tanggal Masuk</Label><Input type="date" value={form.join_date} onChange={(e) => setForm({ ...form, join_date: e.target.value })} /></div>
            <div><Label>Gaji Pokok (Rp/bulan)</Label><Input type="number" value={form.basic_salary} onChange={(e) => setForm({ ...form, basic_salary: e.target.value })} data-testid="emp-salary" /></div>
            <div>
              <Label>Status PTKP</Label>
              <Select value={form.marital_status} onValueChange={(v) => setForm({ ...form, marital_status: v })}>
                <SelectTrigger data-testid="emp-marital"><SelectValue /></SelectTrigger>
                <SelectContent>{MARITAL_OPTIONS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Bank</Label><Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="BCA / Mandiri / dll" /></div>
            <div><Label>No. Rekening</Label><Input value={form.bank_account_number} onChange={(e) => setForm({ ...form, bank_account_number: e.target.value })} /></div>
            <div><Label>Atas Nama</Label><Input value={form.bank_account_holder} onChange={(e) => setForm({ ...form, bank_account_holder: e.target.value })} /></div>
            <div><Label>No. BPJS Kesehatan</Label><Input value={form.bpjs_kesehatan_no} onChange={(e) => setForm({ ...form, bpjs_kesehatan_no: e.target.value })} /></div>
            <div><Label>No. BPJS Ketenagakerjaan</Label><Input value={form.bpjs_ketenagakerjaan_no} onChange={(e) => setForm({ ...form, bpjs_ketenagakerjaan_no: e.target.value })} /></div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3 bg-slate-50">
              <Label className="cursor-pointer">Karyawan Aktif</Label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button className="bg-navy-900 hover:bg-navy-800 text-white" onClick={save} data-testid="emp-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CardContent></Card>
  );
}

/* ==================== Contracts Tab ==================== */
function ContractsTab() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    employee_id: "", contract_number: "", contract_type: "PKWT",
    start_date: todayISO(), end_date: "", salary: 0, notes: "", file_base64: "", file_name: "",
  });

  const load = () => Promise.all([
    api.get("/hr/contracts").then((r) => setItems(r.data)),
    api.get("/hr/employees").then((r) => setEmployees(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("Ukuran file maks 5MB");
    const reader = new FileReader();
    reader.onload = () => setForm((prev) => ({ ...prev, file_base64: reader.result, file_name: f.name }));
    reader.readAsDataURL(f);
  };

  const save = async () => {
    if (!form.employee_id) return toast.error("Pilih karyawan");
    if (!form.contract_number.trim()) return toast.error("No. Kontrak wajib");
    try {
      await api.post("/hr/contracts", { ...form, salary: Number(form.salary) || 0 });
      toast.success("Kontrak tersimpan");
      setOpen(false);
      setForm({ employee_id: "", contract_number: "", contract_type: "PKWT", start_date: todayISO(), end_date: "", salary: 0, notes: "", file_base64: "", file_name: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus kontrak ini?")) return;
    try { await api.delete(`/hr/contracts/${id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const viewFile = async (cid) => {
    try {
      const { data } = await api.get(`/hr/contracts/${cid}`);
      if (!data.file_base64) return toast.error("Kontrak ini tidak punya lampiran file");
      const w = window.open();
      if (data.file_base64.startsWith("data:")) w.document.write(`<iframe src="${data.file_base64}" style="width:100%;height:100vh;border:0"></iframe>`);
      else w.document.write(`<iframe src="data:application/pdf;base64,${data.file_base64}" style="width:100%;height:100vh;border:0"></iframe>`);
    } catch (e) { toast.error("Gagal load file"); }
  };

  return (
    <Card className="card-elevated"><CardContent className="p-4">
      <div className="flex justify-between mb-3">
        <div className="text-sm text-slate-500">{items.length} kontrak</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-navy-900 hover:bg-navy-800 text-white" data-testid="contract-new-btn"><Plus size={14} className="mr-1" />Tambah Kontrak</Button></DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle className="font-display">Tambah Kontrak Kerja</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="col-span-2">
                <Label>Karyawan</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger data-testid="contract-employee"><SelectValue placeholder="Pilih karyawan" /></SelectTrigger>
                  <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.employee_code} — {e.full_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>No. Kontrak</Label><Input value={form.contract_number} onChange={(e) => setForm({ ...form, contract_number: e.target.value })} data-testid="contract-number" /></div>
              <div>
                <Label>Jenis</Label>
                <Select value={form.contract_type} onValueChange={(v) => setForm({ ...form, contract_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PKWT">PKWT (Kontrak)</SelectItem>
                    <SelectItem value="PKWTT">PKWTT (Tetap)</SelectItem>
                    <SelectItem value="Magang">Magang</SelectItem>
                    <SelectItem value="Harian">Harian</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Mulai</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div><Label>Berakhir</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
              <div className="col-span-2"><Label>Gaji (Rp)</Label><Input type="number" value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} /></div>
              <div className="col-span-2"><Label>Catatan / Klausa Penting</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <div className="col-span-2">
                <Label>Upload PDF Kontrak (opsional, maks 5MB)</Label>
                <Input type="file" accept="application/pdf,image/*" onChange={onFile} data-testid="contract-file" />
                {form.file_name && <div className="text-xs text-emerald-700 mt-1">✓ {form.file_name}</div>}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button className="bg-navy-900 hover:bg-navy-800 text-white" onClick={save} data-testid="contract-save">Simpan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Table data-testid="contract-table">
        <TableHeader><TableRow>
          <TableHead>No. Kontrak</TableHead><TableHead>Karyawan</TableHead><TableHead>Jenis</TableHead>
          <TableHead>Mulai</TableHead><TableHead>Berakhir</TableHead>
          <TableHead className="text-right">Gaji</TableHead><TableHead>File</TableHead><TableHead className="text-right">Aksi</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {items.map((c) => (
            <TableRow key={c.id} data-testid={`contract-row-${c.contract_number}`}>
              <TableCell className="font-mono text-xs font-semibold">{c.contract_number}</TableCell>
              <TableCell>{c.employee_name} <span className="text-xs text-slate-500">({c.employee_code})</span></TableCell>
              <TableCell><Badge className="bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-100">{c.contract_type}</Badge></TableCell>
              <TableCell>{formatDate(c.start_date)}</TableCell>
              <TableCell>{c.end_date ? formatDate(c.end_date) : <span className="text-slate-400">-</span>}</TableCell>
              <TableCell className="text-right font-mono">{formatIDR(c.salary)}</TableCell>
              <TableCell>{c.file_name ? <Button size="sm" variant="outline" onClick={() => viewFile(c.id)} data-testid={`contract-view-${c.contract_number}`}><Eye size={12} className="mr-1" />Lihat</Button> : <span className="text-xs text-slate-400">-</span>}</TableCell>
              <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => remove(c.id)}><Trash2 size={14} className="text-red-500" /></Button></TableCell>
            </TableRow>
          ))}
          {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Belum ada kontrak</TableCell></TableRow>}
        </TableBody>
      </Table>
    </CardContent></Card>
  );
}

/* ==================== Attendance Tab ==================== */
function AttendanceTab() {
  const [employees, setEmployees] = useState([]);
  const [month, setMonth] = useState(currentMonth());
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [inputOpen, setInputOpen] = useState(false);
  const [date, setDate] = useState(todayISO());
  const [entries, setEntries] = useState({}); // {empId: {status, check_in, check_out, notes}}

  const load = () => Promise.all([
    api.get("/hr/employees").then((r) => setEmployees(r.data.filter((e) => e.active))),
    api.get(`/hr/attendance/summary?month=${month}`).then((r) => setSummary(r.data)),
    api.get(`/hr/attendance?month=${month}`).then((r) => setRecords(r.data)),
  ]);
  useEffect(() => { load(); }, [month]);

  const openInput = () => {
    // Pre-populate today's existing entries
    const map = {};
    records.filter((r) => r.date === date).forEach((r) => {
      map[r.employee_id] = { status: r.status, check_in: r.check_in, check_out: r.check_out, notes: r.notes };
    });
    employees.forEach((e) => { if (!map[e.id]) map[e.id] = { status: "hadir", check_in: "08:00", check_out: "17:00", notes: "" }; });
    setEntries(map);
    setInputOpen(true);
  };

  const bulkSave = async () => {
    const list = Object.entries(entries).map(([emp_id, v]) => ({
      employee_id: emp_id, date, status: v.status,
      check_in: v.check_in || "", check_out: v.check_out || "", notes: v.notes || "",
    }));
    try {
      const { data } = await api.post("/hr/attendance", { entries: list });
      toast.success(`Absensi tersimpan (${data.saved} record)`);
      setInputOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  return (
    <Card className="card-elevated"><CardContent className="p-4">
      <div className="flex flex-wrap justify-between items-end gap-3 mb-3">
        <div className="flex items-end gap-2">
          <div><Label className="text-xs">Periode</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="att-month" /></div>
          <div><Label className="text-xs">Tanggal Input</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" data-testid="att-date" /></div>
        </div>
        <Button onClick={openInput} className="bg-navy-900 hover:bg-navy-800 text-white" data-testid="att-input-btn"><Plus size={14} className="mr-1" />Input Absensi Harian</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4">
        {["hadir", "sakit", "izin", "alpha", "cuti", "libur"].map((s) => {
          const total = (summary?.rows || []).reduce((sum, r) => sum + (r[s] || 0), 0);
          return (
            <Card key={s} className="card-elevated"><CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">{s}</div>
              <div className="font-display text-lg font-extrabold text-navy-900">{total}</div>
            </CardContent></Card>
          );
        })}
      </div>

      <div className="overflow-x-auto">
        <Table data-testid="att-summary-table">
          <TableHeader><TableRow>
            <TableHead>No. Kry</TableHead><TableHead>Nama</TableHead><TableHead>Dept</TableHead>
            <TableHead className="text-center">Hadir</TableHead><TableHead className="text-center">Sakit</TableHead>
            <TableHead className="text-center">Izin</TableHead><TableHead className="text-center">Alpha</TableHead>
            <TableHead className="text-center">Cuti</TableHead><TableHead className="text-center">Libur</TableHead>
            <TableHead className="text-center">Total</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {summary?.rows?.map((r) => (
              <TableRow key={r.employee_id} data-testid={`att-row-${r.employee_code}`}>
                <TableCell className="font-mono text-xs">{r.employee_code}</TableCell>
                <TableCell>{r.full_name}</TableCell>
                <TableCell>{r.department || "-"}</TableCell>
                <TableCell className="text-center font-mono text-emerald-700">{r.hadir}</TableCell>
                <TableCell className="text-center font-mono">{r.sakit}</TableCell>
                <TableCell className="text-center font-mono">{r.izin}</TableCell>
                <TableCell className="text-center font-mono text-red-700">{r.alpha}</TableCell>
                <TableCell className="text-center font-mono">{r.cuti}</TableCell>
                <TableCell className="text-center font-mono">{r.libur}</TableCell>
                <TableCell className="text-center font-mono font-bold">{r.total_recorded}</TableCell>
              </TableRow>
            ))}
            {(!summary || summary.rows.length === 0) && <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-400">Belum ada data</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={inputOpen} onOpenChange={setInputOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Input Absensi · {formatDate(date)}</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-500 mb-2">Set status masing-masing karyawan. Data existing untuk tanggal ini akan di-overwrite.</div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Karyawan</TableHead><TableHead className="w-32">Status</TableHead>
              <TableHead className="w-24">Masuk</TableHead><TableHead className="w-24">Keluar</TableHead>
              <TableHead>Catatan</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {employees.map((e) => {
                const v = entries[e.id] || { status: "hadir", check_in: "", check_out: "", notes: "" };
                const upd = (patch) => setEntries((prev) => ({ ...prev, [e.id]: { ...v, ...patch } }));
                return (
                  <TableRow key={e.id}>
                    <TableCell><div className="font-medium">{e.full_name}</div><div className="text-xs text-slate-500 font-mono">{e.employee_code}</div></TableCell>
                    <TableCell>
                      <Select value={v.status} onValueChange={(x) => upd({ status: x })}>
                        <SelectTrigger data-testid={`att-status-${e.employee_code}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{ATT_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell><Input type="time" value={v.check_in || ""} onChange={(x) => upd({ check_in: x.target.value })} /></TableCell>
                    <TableCell><Input type="time" value={v.check_out || ""} onChange={(x) => upd({ check_out: x.target.value })} /></TableCell>
                    <TableCell><Input value={v.notes || ""} onChange={(x) => upd({ notes: x.target.value })} /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInputOpen(false)}>Batal</Button>
            <Button className="bg-navy-900 hover:bg-navy-800 text-white" onClick={bulkSave} data-testid="att-bulk-save">Simpan Semua</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail records */}
      {records.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-semibold text-slate-700 mb-2">Rincian Harian ({records.length} record)</div>
          <div className="max-h-64 overflow-y-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Tanggal</TableHead><TableHead>Karyawan</TableHead><TableHead>Status</TableHead>
                <TableHead>Masuk</TableHead><TableHead>Keluar</TableHead><TableHead>Catatan</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {records.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs">{formatDate(r.date)}</TableCell>
                    <TableCell className="text-xs">{r.employee_name}</TableCell>
                    <TableCell><Badge className={ATT_COLOR[r.status]}>{r.status.toUpperCase()}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{r.check_in || "-"}</TableCell>
                    <TableCell className="font-mono text-xs">{r.check_out || "-"}</TableCell>
                    <TableCell className="text-xs text-slate-500">{r.notes || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </CardContent></Card>
  );
}

/* ==================== Payroll Tab ==================== */
function PayrollTab() {
  const [employees, setEmployees] = useState([]);
  const [runs, setRuns] = useState([]);
  const [company, setCompany] = useState({});
  const [period, setPeriod] = useState(currentMonth());
  const [items, setItems] = useState({}); // {empId: {selected, allowances: [], additional_deductions: []}}
  const [preview, setPreview] = useState(null);
  const [detail, setDetail] = useState(null);
  const [pdfPrev, setPdfPrev] = useState({ open: false, url: "", filename: "", title: "" });

  const load = () => Promise.all([
    api.get("/hr/employees").then((r) => {
      const emps = r.data.filter((e) => e.active);
      setEmployees(emps);
      // init selection with all
      setItems((prev) => {
        const next = {};
        emps.forEach((e) => { next[e.id] = prev[e.id] || { selected: true, allowances: [], additional_deductions: [] }; });
        return next;
      });
    }),
    api.get("/hr/payroll").then((r) => setRuns(r.data)),
    api.get("/company-profile").then((r) => setCompany(r.data)).catch(() => {}),
  ]);
  useEffect(() => { load(); }, []);

  const upd = (empId, patch) => setItems((prev) => ({ ...prev, [empId]: { ...prev[empId], ...patch } }));
  const addAllow = (empId) => upd(empId, { allowances: [...items[empId].allowances, { name: "Tunjangan", amount: 0, taxable: true }] });
  const rmAllow = (empId, i) => upd(empId, { allowances: items[empId].allowances.filter((_, idx) => idx !== i) });
  const updAllow = (empId, i, patch) => {
    const list = [...items[empId].allowances];
    list[i] = { ...list[i], ...patch };
    upd(empId, { allowances: list });
  };
  const addDeduct = (empId) => upd(empId, { additional_deductions: [...items[empId].additional_deductions, { name: "Potongan", amount: 0 }] });
  const rmDeduct = (empId, i) => upd(empId, { additional_deductions: items[empId].additional_deductions.filter((_, idx) => idx !== i) });
  const updDeduct = (empId, i, patch) => {
    const list = [...items[empId].additional_deductions];
    list[i] = { ...list[i], ...patch };
    upd(empId, { additional_deductions: list });
  };

  const buildPayload = () => ({
    period,
    items: Object.entries(items).filter(([_, v]) => v.selected).map(([emp_id, v]) => ({
      employee_id: emp_id,
      allowances: v.allowances.map((a) => ({ ...a, amount: Number(a.amount) || 0 })),
      additional_deductions: v.additional_deductions.map((d) => ({ ...d, amount: Number(d.amount) || 0 })),
    })),
  });

  const doPreview = async () => {
    const payload = buildPayload();
    if (payload.items.length === 0) return toast.error("Pilih minimal 1 karyawan");
    try {
      const { data } = await api.post("/hr/payroll/preview", payload);
      setPreview(data);
      toast.success("Preview payroll dihitung");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const doRun = async () => {
    if (!window.confirm(`Jalankan payroll ${period}? Aksi ini akan post jurnal & tidak bisa diubah (hanya dihapus).`)) return;
    const payload = buildPayload();
    try {
      const { data } = await api.post("/hr/payroll/run", payload);
      toast.success(`Payroll ${data.period} berhasil dijalankan · Take home ${new Intl.NumberFormat("id-ID").format(data.total_take_home)}`);
      setPreview(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const openDetail = async (rid) => {
    try { const { data } = await api.get(`/hr/payroll/${rid}`); setDetail(data); }
    catch (e) { toast.error("Gagal load detail"); }
  };

  const remove = async (rid) => {
    if (!window.confirm("Hapus payroll ini? Jurnal juga akan di-reverse.")) return;
    try { await api.delete(`/hr/payroll/${rid}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const emailAll = async (rid) => {
    if (!window.confirm("Kirim email slip gaji ke SEMUA karyawan pada payroll ini? Karyawan tanpa email akan di-skip.")) return;
    try {
      const { data } = await api.post(`/hr/payroll/${rid}/email-slips`, {});
      toast.success(`Email terkirim: ${data.sent} · Gagal: ${data.failed} · Tidak ada email: ${data.skipped}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal kirim email"); }
  };

  const emailOne = async (slip) => {
    if (!detail) return;
    try {
      const { data } = await api.post(`/hr/payroll/${detail.id}/email-slips`, { employee_id: slip.employee_id });
      if (data.sent > 0) toast.success(`Slip terkirim ke ${slip.employee_name}`);
      else if (data.skipped > 0) toast.error(`${slip.employee_name} belum punya email`);
      else toast.error("Gagal kirim email — cek log");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const printSlip = (slip) => {
    try {
      const res = generatePayslipPDF({ slip, run: detail, company, preview: true });
      if (res?.url) setPdfPrev({ open: true, url: res.url, filename: res.filename, title: `Preview Slip Gaji ${slip.employee_code}` });
    }
    catch (e) { toast.error("Gagal cetak slip: " + e.message); }
  };

  return (
    <div className="space-y-4">
      <Card className="card-elevated"><CardContent className="p-4">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
          <div>
            <div className="text-sm font-semibold text-slate-800">Generate Payroll Bulanan</div>
            <div className="text-xs text-slate-500">Pilih karyawan, isi tunjangan & potongan tambahan, preview → jalankan.</div>
          </div>
          <div className="flex items-end gap-2">
            <div><Label className="text-xs">Periode</Label><Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="w-40" data-testid="payroll-period" /></div>
            <Button variant="outline" onClick={doPreview} data-testid="payroll-preview-btn">Preview Perhitungan</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={doRun} data-testid="payroll-run-btn"><Banknote size={14} className="mr-1" />Jalankan Payroll</Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table data-testid="payroll-emp-table">
            <TableHeader><TableRow>
              <TableHead className="w-12"></TableHead><TableHead>Karyawan</TableHead>
              <TableHead>PTKP</TableHead><TableHead className="text-right">Gaji Pokok</TableHead>
              <TableHead>Tunjangan</TableHead><TableHead>Potongan Tambahan</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {employees.map((e) => {
                const v = items[e.id] || { selected: false, allowances: [], additional_deductions: [] };
                return (
                  <TableRow key={e.id}>
                    <TableCell><Switch checked={v.selected} onCheckedChange={(x) => upd(e.id, { selected: x })} data-testid={`payroll-sel-${e.employee_code}`} /></TableCell>
                    <TableCell><div className="font-medium">{e.full_name}</div><div className="text-xs text-slate-500 font-mono">{e.employee_code}</div></TableCell>
                    <TableCell className="font-mono text-xs">{e.marital_status}</TableCell>
                    <TableCell className="text-right font-mono">{formatIDR(e.basic_salary)}</TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {v.allowances.map((a, i) => (
                          <div key={i} className="flex gap-1 items-center">
                            <Input value={a.name} onChange={(x) => updAllow(e.id, i, { name: x.target.value })} className="h-7 text-xs" />
                            <Input type="number" value={a.amount} onChange={(x) => updAllow(e.id, i, { amount: x.target.value })} className="h-7 w-24 text-xs" />
                            <Switch checked={a.taxable} onCheckedChange={(x) => updAllow(e.id, i, { taxable: x })} title="Kena pajak" />
                            <Button size="sm" variant="ghost" onClick={() => rmAllow(e.id, i)}><Trash2 size={12} className="text-red-500" /></Button>
                          </div>
                        ))}
                        <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => addAllow(e.id)}><Plus size={10} className="mr-1" />Tambah Tunjangan</Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {v.additional_deductions.map((d, i) => (
                          <div key={i} className="flex gap-1 items-center">
                            <Input value={d.name} onChange={(x) => updDeduct(e.id, i, { name: x.target.value })} className="h-7 text-xs" />
                            <Input type="number" value={d.amount} onChange={(x) => updDeduct(e.id, i, { amount: x.target.value })} className="h-7 w-24 text-xs" />
                            <Button size="sm" variant="ghost" onClick={() => rmDeduct(e.id, i)}><Trash2 size={12} className="text-red-500" /></Button>
                          </div>
                        ))}
                        <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => addDeduct(e.id)}><Plus size={10} className="mr-1" />Tambah Potongan</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {employees.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400">Belum ada karyawan aktif</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </CardContent></Card>

      {preview && (
        <Card className="card-elevated border-emerald-300"><CardContent className="p-4">
          <div className="font-display font-bold text-slate-900 mb-2">Preview Payroll {preview.period}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 text-sm">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3"><div className="text-xs text-slate-500">Total Take Home</div><div className="font-mono font-bold text-emerald-700">{formatIDR(preview.total_take_home)}</div></div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3"><div className="text-xs text-slate-500">Total PPh 21</div><div className="font-mono font-bold text-red-700">{formatIDR(preview.total_pph21)}</div></div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3"><div className="text-xs text-slate-500">BPJS Kry</div><div className="font-mono font-bold">{formatIDR(preview.total_bpjs_employee)}</div></div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3"><div className="text-xs text-slate-500">BPJS Perusahaan</div><div className="font-mono font-bold">{formatIDR(preview.total_bpjs_company)}</div></div>
          </div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Karyawan</TableHead><TableHead className="text-right">Bruto</TableHead>
              <TableHead className="text-right">BPJS Kry</TableHead><TableHead className="text-right">PPh 21</TableHead>
              <TableHead className="text-right">Take Home</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {preview.slips.map((s) => (
                <TableRow key={s.employee_id}>
                  <TableCell><div className="font-medium">{s.employee_name}</div><div className="text-xs text-slate-500 font-mono">{s.employee_code}</div></TableCell>
                  <TableCell className="text-right font-mono">{formatIDR(s.gross_taxable)}</TableCell>
                  <TableCell className="text-right font-mono">{formatIDR(s.bpjs.total_employee)}</TableCell>
                  <TableCell className="text-right font-mono">{formatIDR(s.pph21.amount)} <span className="text-xs text-slate-400">({(s.pph21.rate * 100).toFixed(2)}%)</span></TableCell>
                  <TableCell className="text-right font-mono font-bold text-emerald-700">{formatIDR(s.take_home)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent></Card>
      )}

      <Card className="card-elevated"><CardContent className="p-4">
        <div className="font-display font-bold text-slate-900 mb-2">Riwayat Payroll</div>
        <Table data-testid="payroll-history">
          <TableHeader><TableRow>
            <TableHead>Periode</TableHead><TableHead>No. Run</TableHead>
            <TableHead className="text-right">Take Home</TableHead>
            <TableHead className="text-right">PPh 21</TableHead>
            <TableHead className="text-right">BPJS</TableHead>
            <TableHead>Tgl Jalan</TableHead><TableHead className="text-right">Aksi</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {runs.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-mono font-bold">{r.period}</TableCell>
                <TableCell className="font-mono text-xs">{r.run_number}</TableCell>
                <TableCell className="text-right font-mono">{formatIDR(r.total_take_home)}</TableCell>
                <TableCell className="text-right font-mono">{formatIDR(r.total_pph21)}</TableCell>
                <TableCell className="text-right font-mono">{formatIDR(r.total_bpjs_company + r.total_bpjs_employee)}</TableCell>
                <TableCell className="text-xs">{formatDate(r.run_at)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => openDetail(r.id)} data-testid={`payroll-detail-${r.run_number}`}><Eye size={12} className="mr-1" />Detail</Button>
                  <Button size="sm" variant="outline" className="ml-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => emailAll(r.id)} data-testid={`payroll-email-all-${r.run_number}`}><Mail size={12} className="mr-1" />Email Semua</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(r.id)} className="ml-1"><Trash2 size={14} className="text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {runs.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Belum ada payroll</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Detail Payroll {detail?.period}</DialogTitle></DialogHeader>
          {detail && (
            <div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 text-sm">
                <div className="bg-slate-50 border rounded-lg p-2"><div className="text-xs text-slate-500">Take Home</div><div className="font-mono font-bold text-emerald-700">{formatIDR(detail.total_take_home)}</div></div>
                <div className="bg-slate-50 border rounded-lg p-2"><div className="text-xs text-slate-500">PPh 21</div><div className="font-mono font-bold text-red-700">{formatIDR(detail.total_pph21)}</div></div>
                <div className="bg-slate-50 border rounded-lg p-2"><div className="text-xs text-slate-500">BPJS Kry</div><div className="font-mono">{formatIDR(detail.total_bpjs_employee)}</div></div>
                <div className="bg-slate-50 border rounded-lg p-2"><div className="text-xs text-slate-500">BPJS Prshn</div><div className="font-mono">{formatIDR(detail.total_bpjs_company)}</div></div>
              </div>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Karyawan</TableHead><TableHead className="text-right">Gaji Pokok</TableHead>
                  <TableHead className="text-right">Tunjangan</TableHead>
                  <TableHead className="text-right">BPJS Kry</TableHead><TableHead className="text-right">PPh 21</TableHead>
                  <TableHead className="text-right">Take Home</TableHead><TableHead className="text-right">Slip</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {detail.slips.map((s) => (
                    <TableRow key={s.employee_id}>
                      <TableCell><div className="font-medium">{s.employee_name}</div><div className="text-xs text-slate-500 font-mono">{s.employee_code}</div></TableCell>
                      <TableCell className="text-right font-mono">{formatIDR(s.basic_salary)}</TableCell>
                      <TableCell className="text-right font-mono">{formatIDR(s.allowance_total_taxable + s.allowance_total_nontaxable)}</TableCell>
                      <TableCell className="text-right font-mono">{formatIDR(s.bpjs.total_employee)}</TableCell>
                      <TableCell className="text-right font-mono">{formatIDR(s.pph21.amount)}</TableCell>
                      <TableCell className="text-right font-mono font-bold text-emerald-700">{formatIDR(s.take_home)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="sm" variant="outline" onClick={() => printSlip(s)} data-testid={`slip-print-${s.employee_code}`}><Printer size={12} className="mr-1" />PDF</Button>
                        <Button size="sm" variant="outline" className="ml-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => emailOne(s)} data-testid={`slip-email-${s.employee_code}`}><Mail size={12} className="mr-1" />Email</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <PdfPreviewDialog open={pdfPrev.open} url={pdfPrev.url} filename={pdfPrev.filename} title={pdfPrev.title} onClose={() => setPdfPrev({ open: false, url: "", filename: "", title: "" })} />
    </div>
  );
}

/* ==================== HR Settings Tab ==================== */
function HRSettingsTab() {
  const [form, setForm] = useState(null);
  useEffect(() => { api.get("/hr/settings").then((r) => setForm(r.data)); }, []);
  if (!form) return <div className="p-4 text-slate-400">Loading...</div>;

  const save = async () => {
    try {
      const payload = { ...form };
      delete payload.id; delete payload.updated_at;
      Object.keys(payload).forEach((k) => { if (typeof payload[k] === "string" && !isNaN(payload[k])) payload[k] = Number(payload[k]); });
      const { data } = await api.put("/hr/settings", payload);
      setForm(data);
      toast.success("Setting BPJS & PPh 21 disimpan");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const RateInput = ({ field, label, isPercent = true }) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="relative">
        <Input type="number" step="0.0001" value={form[field]} onChange={(e) => setForm({ ...form, [field]: Number(e.target.value) })} data-testid={`hr-${field}`} />
        {isPercent && <span className="absolute right-3 top-2.5 text-xs text-slate-400 font-mono">= {((form[field] || 0) * 100).toFixed(2)}%</span>}
      </div>
    </div>
  );

  return (
    <Card className="card-elevated"><CardContent className="p-4 space-y-4">
      <div>
        <div className="font-display font-bold text-slate-900 mb-1">Iuran BPJS Kesehatan</div>
        <div className="grid grid-cols-3 gap-3">
          <RateInput field="kes_company" label="% Perusahaan" />
          <RateInput field="kes_employee" label="% Karyawan" />
          <div><Label className="text-xs">Batas Upah (Rp)</Label><Input type="number" value={form.kes_max} onChange={(e) => setForm({ ...form, kes_max: Number(e.target.value) })} /></div>
        </div>
      </div>
      <div>
        <div className="font-display font-bold text-slate-900 mb-1">Iuran BPJS Ketenagakerjaan</div>
        <div className="grid grid-cols-3 gap-3">
          <RateInput field="jht_company" label="JHT % Perusahaan" />
          <RateInput field="jht_employee" label="JHT % Karyawan" />
          <RateInput field="jkk_company" label="JKK % Perusahaan (risiko rendah)" />
          <RateInput field="jkm_company" label="JKM % Perusahaan" />
          <RateInput field="jp_company" label="JP % Perusahaan" />
          <RateInput field="jp_employee" label="JP % Karyawan" />
          <div className="col-span-3"><Label className="text-xs">Batas Upah JP (Rp)</Label><Input type="number" value={form.jp_max} onChange={(e) => setForm({ ...form, jp_max: Number(e.target.value) })} /></div>
        </div>
      </div>
      <div>
        <div className="font-display font-bold text-slate-900 mb-1">Metode PPh 21</div>
        <Select value={form.pph21_method} onValueChange={(v) => setForm({ ...form, pph21_method: v })}>
          <SelectTrigger className="w-64" data-testid="hr-pph-method"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ter">TER Bulanan (PMK 168/2023) ⭐</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs text-slate-500 mt-2">Sistem menghitung PPh 21 berdasarkan Tarif Efektif Rata-rata (TER) sesuai kategori PTKP (A/B/C) untuk masing-masing karyawan.</div>
      </div>
      <div className="flex justify-end">
        <Button className="bg-navy-900 hover:bg-navy-800 text-white" onClick={save} data-testid="hr-settings-save">Simpan Setting</Button>
      </div>
    </CardContent></Card>
  );
}
